import React, { useEffect } from "react"
import { Formik, Form } from "formik"
import { Typography, Stack, TextField, Paper } from "@mui/material"
import { packagingNature, packagingCardOrder } from "../../../utils/packaging"
import { COLORS, KFC_BRANDS } from "../../../utils"
import { packagingsRealNumbersSchema } from "../../../utils/yupValidators"

/**
 * Builds the initial values structure for the Formik form.
 * Structure:
 * {[packagingType]: {
 *     [brandName]: number | string
 *   }}
 *
 * - For DISPOSABLE packaging types:
 *   → Use the saved `realNumber` if it exists (or an empty string if not),
 *     because the user is expected to manually input the number of packages produced.
 * - For other packaging types (REUSABLE, CAPPED):
 *   → Pre-fill with the `theoreticalNumber`, as these are not meant to be manually changed.
 */
const getInitialValues = (packagings) => {
  const initialValues = {}
  packagings.forEach(packaging => {
    initialValues[packaging.type] = {}
    packaging.brands.forEach(brand => {
      initialValues[packaging.type][brand.brand] = packaging.type !== "DISPOSABLE" ? brand.theoreticalNumber : brand.realNumber ?? ""
    })
  })
  return initialValues
}

const PackagingCountingForm = ({ packagings, onChange, isValidForm }) => {
  return (
    <Formik
      initialValues={getInitialValues(packagings)}
      validationSchema={packagingsRealNumbersSchema(packagings)}
      validateOnMount
      enableReinitialize
    >
      {({ values, handleChange, handleBlur, errors, touched, isValid }) => {
      // Notify the parent component whenever the form's validity changes (used to enable/disable the submit button)
      useEffect(() => {
          if (isValidForm) isValidForm(isValid)
        }, [isValid, isValidForm])

        // Convert the current Formik values into a flat array of packaging updates,
        // and pass it to the parent via the onChange callback.
        useEffect(() => {
          const updates = []
          packagings.forEach(packaging => {
            packaging.brands.forEach(brand => {
              const type = packaging.type
              const name = brand.brand
              updates.push({
                packagingId: packaging.packaging.objectId,
                brand: brand.brand,
                realNumber: Number(values[type][name]) || 0
              })
            })
          })
          onChange(updates)
        }, [values])

        return (
          <Form>
            <Stack spacing={3}>
              {[...packagingCardOrder].reverse().map(packagingType => {
                const packaging = packagings.find(p => p.type === packagingType)
                if (!packaging) return null

                const packagingLabel = packagingNature.find(n => n.value === packagingType)?.label
                const packagingImg = packagingNature.find(n => n.value === packagingType)?.img

                return (
                  <Paper key={packagingType} sx={styles.paper(packagingType)}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <img src={packagingImg} alt={packagingType} width={48} height={48} />
                        <Typography sx={styles.headerText}>
                          {packagingLabel}
                        </Typography>
                      </Stack>
                      <Typography sx={styles.columnLabel}>Réalisées / prévues</Typography>
                    </Stack>

                    <Stack spacing={2} sx={styles.innerBox}>
                      {packaging.brands.map((brand, brandIndex) => {
                        const brandData = KFC_BRANDS.find(b => b.name === brand.brand)
                        const value = values?.[packagingType]?.[brand.brand] ?? ""
                        
                        return (
                        <Stack key={brandIndex} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <img src={brandData?.image} alt={brand.brand} width={32} height={32} />
                            <Typography sx={styles.brandText}>
                              {brandData?.label}
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {touched?.[packagingType]?.[brand.brand] && errors?.[packagingType]?.[brand.brand] && (
                              <Typography variant="caption" color="error" sx={styles.errorText}>
                                {errors[packagingType][brand.brand]}
                              </Typography>
                            )}
                            <TextField
                              type="number"
                              variant="outlined"
                              name={`${packagingType}.${brand.brand}`}
                              value={value}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                min: 0,
                                sx: styles.inputProps
                              }}
                              InputProps={{
                                sx: styles.inputWrapper
                              }}
                              sx={styles.textField}
                            />
                            <Typography variant="body2" sx={styles.theoreticalNumber}>
                              / {brand.theoreticalNumber}
                            </Typography>
                          </Stack>
                        </Stack>
                      )})}
                    </Stack>
                  </Paper>
                )
              })}
            </Stack>
          </Form>
        )
      }}
    </Formik>
  )
}

const getBackgroundColor = (packagingType) => {
  switch (packagingType) {
    case "DISPOSABLE":
      return COLORS.ORANGE_100
    case "REUSABLE":
      return COLORS.GREEN_SUCCESS_100
    case "CAPPED":
      return COLORS.DARK_BLUE_50
    default:
      return "white"
  }
}

const styles = {
  paper: (packagingType) => ({
    p: 3,
    backgroundColor: getBackgroundColor(packagingType),
    boxShadow: "none",
    borderRadius: "8px"
  }),
  headerText: {
    fontSize: "18px",
    lineHeight: 1.4,
    fontWeight: 500,
    color: COLORS.BLACK800
  },
  columnLabel: {
    fontSize: "16px",
    lineHeight: 1.5,
    fontWeight: 400,
    color: COLORS.BLACK800
  },
  innerBox: {
    marginTop: "16px",
    backgroundColor: COLORS.WHITE,
    borderRadius: "8px",
    width: "100%",
    padding: "16px"
  },
  brandText: {
    fontSize: "16px",
    lineHeight: 1.5,
    fontWeight: 400,
    color: COLORS.BLACK_700
  },
  inputProps: {
    padding: "8px 16px",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px"
  },
  inputWrapper: {
    height: "40px",
    borderRadius: "8px"
  },
  textField: {
    width: "81px"
  },
  theoreticalNumber: {
    minWidth: "52px",
    color: COLORS.BLACK_700
  },
  errorText: {
    marginLeft: 1,
    marginTop: "4px"
  }
}

export default PackagingCountingForm